/**
 * UsersService
 * -------------------
 *
 * Servicio de acceso a datos de usuarios pensado como capa de "data layer" con caché.
 * Se apoya en ParticipantService / RegistrationsService para hablar con el backend y en
 * CacheService para persistir datos en localStorage.
 *
 * FUNCIONALIDAD PRINCIPAL
 * - Obtener el usuario actual y adaptarlo al modelo interno `User`.
 * - Cargar usuarios reales por escudería (`getEscuderiaUsers`) y por agencia
 *   (`getAgenciaUsers`), aplicando paginación basada en `totalUsers`.
 * - Excluir siempre los usuarios virtuales (tags de tipo `AGENCIA` y `ESCUDERIA`).
 * - Cargar escuderías y agencias virtuales (`getEscuderias`, `getAgencias`).
 * - Cargar todos los usuarios reales a partir de una lista de escuderías (`getAllUsers`).
 *
 * ESTRATEGIA DE CACHÉ
 * - Los usuarios reales se almacenan en un `Map<string, User>` serializado en localStorage
 *   bajo la key `__users` (vía `getFromCache` / `saveInCache`).
 * - Las escuderías virtuales se cachean en `__escuderias` y las agencias virtuales en
 *   `__agencias`.
 * - La metadata de caché (`CacheMetadata`) se guarda en `__users_metadata` e indica:
 *     - `escuderias`: qué escuderías se han cargado completamente.
 *     - `agencias`: qué agencias (agencia_escuderia) se han cargado completamente.
 *     - `all`: si ya se cargaron todos los usuarios reales.
 * - Antes de hacer peticiones se consulta la metadata para evitar fetches redundantes y,
 *   si la entidad ya está marcada como cargada, se devuelven los datos desde caché.
 *
 * ADAPTACIÓN DE DATOS
 * - `userAdapter` transforma `Participant` → `User`, resolviendo:
 *     - `escuderia` y `agencia` a partir de los tags con prefijos.
 *     - `userType` buscando cualquier tag que coincida con `USER_TYPES`.
 *     - `totalUsers` numérico a partir de `participant.total`.
 *     - avatar por defecto cuando no viene definido en backend.
 */

import { inject, Injectable } from '@angular/core'
import { firstValueFrom, forkJoin, map } from 'rxjs'
import { ParticipantService, RegistrationsService } from 'superlikers-microsites-api'

import { CacheService } from '@shared/services/cache.service'
import { TAG_PREFIX_AGENCIA, TAG_PREFIX_ESCUDERIA } from '@core/utils/users.utils'
import { CacheMetadata, Participant, User, USER_TYPES, UserCacheEntry } from '../interfaces/users.interface'

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  #participantService = inject(ParticipantService)
  #registrationsService = inject(RegistrationsService)

  #cacheService = inject(CacheService)

  #USERS_CACHE_KEY = '__users'
  #ESCUDERIAS_CACHE_KEY = '__escuderias'
  #AGENCIAS_CACHE_KEY = '__agencias'
  #METADATA_CACHE_KEY = '__users_metadata'

  async getCurrentUser() {
    const response$ = this.#participantService.getParticipant<Participant>()
    const response = await firstValueFrom(response$)

    return this.userAdapter(response.participation)
  }

  async getAllUsers(escuderias: User[]) {
    const cached = await this.getFromCache()
    const metadata = await this.getMetadataFromCache()

    if (metadata.all) return Array.from(cached.values())

    const promises = escuderias.map(escuderia => this.getEscuderiaUsers(escuderia.escuderia!, escuderia.totalUsers!))
    const usersWrapper = await Promise.all(promises)
    const users = usersWrapper.flat()

    this.saveInCache(users, cached)

    metadata.all = true
    await this.saveMetadataToCache(metadata)

    return users
  }

  async getEscuderiaUsers(escuderiaName: string, totalUsers: number) {
    const cached = await this.getFromCache()
    const metadata = await this.getMetadataFromCache()

    const escuderiaTag = `${TAG_PREFIX_ESCUDERIA}${escuderiaName}`
    const pages = Math.ceil(totalUsers / 30)

    const isFullyLoaded = metadata.escuderias.includes(escuderiaName)

    if (!isFullyLoaded) {
      const users = await this.fetchUsersByTags([escuderiaTag], pages)
      const filteredUsers = users.filter(
        u =>
          u.tags.includes(escuderiaTag) &&
          !u.tags.includes(USER_TYPES.AGENCIA) && // Excluir usuario virtual
          !u.tags.includes(USER_TYPES.ESCUDERIA) // Excluir usuario virtual
      )

      this.saveInCache(filteredUsers, cached)

      metadata.escuderias.push(escuderiaName)
      await this.saveMetadataToCache(metadata)

      return filteredUsers
    }

    const cachedUsers = Array.from(cached.values()).filter(u => u.escuderia === escuderiaName)
    return cachedUsers
  }

  async getAgenciaUsers(agenciaName: string, escuderiaName: string, totalUsers: number) {
    const cached = await this.getFromCache()
    const metadata = await this.getMetadataFromCache()

    const agenciaTag = `${TAG_PREFIX_AGENCIA}${agenciaName}`
    const escuderiaTag = `${TAG_PREFIX_ESCUDERIA}${escuderiaName}`
    const pages = Math.ceil(totalUsers / 30)

    const agenciaKey = `${agenciaName}_${escuderiaName}`
    const isFullyLoaded = metadata.agencias.includes(agenciaKey)

    if (!isFullyLoaded) {
      const users = await this.fetchUsersByTags([agenciaTag], pages)
      const filteredUsers = users.filter(
        u =>
          u.tags.includes(agenciaTag) &&
          u.tags.includes(escuderiaTag) &&
          !u.tags.includes(USER_TYPES.AGENCIA) && // Excluir usuario virtual
          !u.tags.includes(USER_TYPES.ESCUDERIA) // Excluir usuario virtual
      )

      console.log({ agenciaTag, escuderiaTag, users, filteredUsers })
      this.saveInCache(filteredUsers, cached)

      metadata.agencias.push(agenciaKey)
      await this.saveMetadataToCache(metadata)

      return filteredUsers
    }

    const cachedUsers = Array.from(cached.values()).filter(
      u => u.agencia === agenciaName && u.escuderia === escuderiaName
    )
    return cachedUsers
  }

  async getEscuderias() {
    const cached$ = this.#cacheService.getFromCache<User[]>(this.#ESCUDERIAS_CACHE_KEY)
    const cached = await firstValueFrom(cached$)

    if (!cached) {
      const users = await this.fetchUsersByTags([USER_TYPES.ESCUDERIA])
      this.#cacheService.saveToCache(this.#ESCUDERIAS_CACHE_KEY, users)

      return users
    }

    return cached
  }

  async getAgencias() {
    const cached$ = this.#cacheService.getFromCache<User[]>(this.#AGENCIAS_CACHE_KEY)
    const cached = await firstValueFrom(cached$)

    if (!cached) {
      const users = await this.fetchUsersByTags([USER_TYPES.AGENCIA])
      this.#cacheService.saveToCache(this.#AGENCIAS_CACHE_KEY, users)

      return users
    }

    return cached
  }

  async updateAvatar(url: string) {
    const data = { avatar: url }

    const response$ = this.#registrationsService.updateParticipant(data)
    const response = await firstValueFrom(response$)

    return response
  }

  async updateUser(data: Record<string, unknown>) {
    const response$ = this.#registrationsService.updateParticipant(data)
    const response = await firstValueFrom(response$)

    return response
  }

  private async fetchUsersByTags(tags: string[], pages = 1) {
    const requests$ = []
    for (let i = 1; i <= pages; i++) {
      const request$ = this.#participantService
        .getList<Participant>({ page: i, with_admins: true, tags })
        .pipe(map(response => response.data.filter(user => user.staff !== 'OK')))

      requests$.push(request$)
    }

    const response$ = forkJoin(requests$)
    const response = await firstValueFrom(response$)

    const users = response.flat().map(this.userAdapter)
    return users
  }

  private userAdapter(participant: Participant): User {
    const { tags } = participant

    const escuderiaTag = tags.find(tag => tag.startsWith(TAG_PREFIX_ESCUDERIA))
    const agenciaTag = tags.find(tag => tag.startsWith(TAG_PREFIX_AGENCIA))

    const escuderia = escuderiaTag?.replace(TAG_PREFIX_ESCUDERIA, '') ?? null
    const agencia = agenciaTag?.replace(TAG_PREFIX_AGENCIA, '') ?? null

    const userType = tags.find(tag => Object.values(USER_TYPES).includes(tag as USER_TYPES))

    const defaultAvatar = 'https://secure.gravatar.com/avatar/f7219bbd71f453e41750fa3c63a11da1?size=64&default=mm'

    return {
      uid: participant.uid,
      authenticated: participant.authenticated,
      avatar: participant.avatar ?? defaultAvatar,
      name: participant.name,
      email: participant.email,
      cargo: participant.cargo,
      totalUsers: Number(participant.total) || 0,
      escuderia,
      agencia,
      tags,
      userType: userType as USER_TYPES
    }
  }

  private async getFromCache() {
    const cached$ = this.#cacheService.getFromCache<UserCacheEntry[]>(this.#USERS_CACHE_KEY)
    const cached = await firstValueFrom(cached$)

    if (!cached) return new Map<string, User>()
    return new Map(cached)
  }

  private saveInCache(newUsers: User[], cached?: Map<string, User>) {
    const map = cached ? cached : new Map<string, User>()
    newUsers.forEach(user => map.set(user.uid, user))

    this.#cacheService.saveToCache(this.#USERS_CACHE_KEY, [...map.entries()])
  }

  private async getMetadataFromCache(): Promise<CacheMetadata> {
    const cached$ = this.#cacheService.getFromCache<CacheMetadata>(this.#METADATA_CACHE_KEY)
    const cached = await firstValueFrom(cached$)

    if (!cached) {
      return { escuderias: [], agencias: [] }
    }

    return cached
  }

  private async saveMetadataToCache(metadata: CacheMetadata): Promise<void> {
    this.#cacheService.saveToCache(this.#METADATA_CACHE_KEY, metadata)
  }
}
